import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tooltip,
} from '@/components/ui';
import { DEFAULT_QUERY_LIMIT } from '@shared/constants';
import { cn } from '@/lib/utils';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Filter,
  GitBranch,
  Hash,
  Play,
  Plus,
  Save,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { QueryConditionRow } from '../query-condition-row';
import { QueryOrderByRow } from '../query-order-by-row';
import { QueryPreview } from '../query-preview';
import { type QueryBuilderPanelProps, useQueryBuilderPanel } from './use-query-builder-panel';

// Spacing: one horizontal padding for header and content; vertical by role
const CARD_HEADER_CLASS = 'flex flex-row items-center space-y-0 px-4 py-2 border-b';
const CARD_CONTENT_WITH_DIVIDERS_CLASS = 'p-0 [&>div]:border-t [&>div:first-child]:border-t-0';
const CONTENT_PX = 'px-4';
const ROW_BLOCK_CLASS = 'py-3';
const ROW_HOVER = 'hover:bg-muted/20 transition-colors';
const ENTRIES_GAP = 'gap-2';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function isUrl(s: string): boolean {
  return s.startsWith('http://') || s.startsWith('https://');
}

function QueryErrorContent({ text }: { text: string }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) =>
        isUrl(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
          >
            {part}
          </a>
        ) : (
          part
        )
      )}
    </>
  );
}

export function QueryBuilderPanel(props: QueryBuilderPanelProps) {
  const f = useQueryBuilderPanel(props);

  return (
    <Sheet open={f.open} onOpenChange={f.onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Query Builder</SheetTitle>
          <SheetDescription>
            Build and execute Firestore queries for the{' '}
            <strong>{f.collectionName || 'selected collection'}</strong> collection.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* 1. Filters: one Card, divider rows */}
          <Card>
            <CardHeader className={CARD_HEADER_CLASS}>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                Filters ({f.activeFilterCount} active)
              </CardTitle>
            </CardHeader>
            <CardContent className={CARD_CONTENT_WITH_DIVIDERS_CLASS}>
              <div className={cn(CONTENT_PX, ROW_BLOCK_CLASS, ROW_HOVER)}>
                <Label htmlFor="document-id-prefix" className="text-muted-foreground text-sm">
                  Document ID prefix
                </Label>
                <Input
                  id="document-id-prefix"
                  placeholder="Filter by document ID prefix…"
                  value={f.documentIdPrefix}
                  onChange={(e) => f.onDocumentIdPrefixChange(e.target.value)}
                  className="mt-1.5 w-full"
                />
              </div>

              <div className={cn(CONTENT_PX, ROW_BLOCK_CLASS)}>
                <div className="pb-3">
                  <span className="text-sm font-medium ">All Of These (AND)</span>
                </div>
                <div className={cn('flex flex-col', ENTRIES_GAP)}>
                  {f.conditions.map((condition, index) => (
                    <div key={index} className={cn(ROW_HOVER, 'rounded-sm')}>
                      <QueryConditionRow
                        condition={condition}
                        index={index}
                        availableFields={f.availableFields}
                        onChange={f.updateCondition}
                        onRemove={f.removeCondition}
                      />
                    </div>
                  ))}
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={f.addCondition}
                      className="h-auto py-1.5 px-0 text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add condition
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <div className={cn(CONTENT_PX, ROW_BLOCK_CLASS)}>
                  <div className="pb-4">
                    <div className="flex items-center justify-between w-full">
                      <span className="text-sm font-medium">Any Of These (OR)</span>
                      {f.orConditions.length === 1 && (
                        <span className="text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded">
                          Add at least 2 conditions to use OR logic
                        </span>
                      )}
                      {f.orConditions.length >= 2 && (
                        <span className="text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded">
                          {f.orConditions.length} conditions
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Advanced — Firestore constraints apply
                    </p>
                  </div>
                  <div className={cn('flex flex-col', ENTRIES_GAP)}>
                    {f.showOrError && f.error && (
                      <div className="bg-destructive/10 text-destructive text-sm rounded-sm py-2 px-2">
                        {f.error.split('\n').map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}
                      </div>
                    )}
                    {f.orConditions.length === 0 && (
                      <p className="text-sm text-muted-foreground py-2">
                        No OR conditions added. Add at least 2 conditions to use OR logic.
                      </p>
                    )}
                    {f.orConditions.map((condition, index) => (
                      <div key={index} className={cn(ROW_HOVER, 'rounded-sm')}>
                        <QueryConditionRow
                          condition={condition}
                          index={index}
                          availableFields={f.availableFields}
                          onChange={f.updateOrCondition}
                          onRemove={f.removeOrCondition}
                        />
                      </div>
                    ))}
                    <div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={f.addOrCondition}
                        className="h-auto py-1.5 px-0 text-muted-foreground hover:text-foreground"
                      >
                        <GitBranch className="h-4 w-4 mr-2" />
                        Add OR condition
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2. Order By */}
          <Card>
            <CardHeader className={CARD_HEADER_CLASS}>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                Order By
              </CardTitle>
            </CardHeader>
            <CardContent className={CARD_CONTENT_WITH_DIVIDERS_CLASS}>
              <div className={cn(CONTENT_PX, ROW_BLOCK_CLASS)}>
                <div className="pb-2">
                  {f.orderBy.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No ordering specified</div>
                  ) : (
                    <div className={cn('flex flex-col', ENTRIES_GAP)}>
                      {f.orderBy.map((order, index) => (
                        <div key={index} className={cn(ROW_HOVER, 'rounded-sm')}>
                          <QueryOrderByRow
                            order={order}
                            index={index}
                            availableFields={f.availableFields}
                            onChange={f.updateOrderBy}
                            onRemove={f.removeOrderBy}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={f.addOrderBy}
                  className="h-auto py-1.5 px-0 text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add sort rule
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 3. Limit */}
          <Card>
            <CardHeader className={CARD_HEADER_CLASS}>
              <CardTitle className="text-base flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                Limit
              </CardTitle>
            </CardHeader>
            <CardContent className={CARD_CONTENT_WITH_DIVIDERS_CLASS}>
              <div className={cn(CONTENT_PX, ROW_BLOCK_CLASS, ROW_HOVER)}>
                <Input
                  id="limit"
                  type="number"
                  min="1"
                  value={f.limit}
                  onChange={(e) => f.setLimit(Number(e.target.value) || DEFAULT_QUERY_LIMIT)}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* 4. Saved Queries */}
          {f.hasSavedQueries && (
            <Card>
              <CardHeader className={CARD_HEADER_CLASS}>
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="h-4 w-4 text-muted-foreground" />
                  Saved Queries
                </CardTitle>
              </CardHeader>
              <CardContent className={CARD_CONTENT_WITH_DIVIDERS_CLASS}>
                {f.savedQueries.map((saved) => (
                  <div
                    key={saved.name}
                    className={cn(
                      'flex items-center justify-between',
                      CONTENT_PX,
                      ROW_BLOCK_CLASS,
                      'text-sm',
                      ROW_HOVER
                    )}
                  >
                    <span className="flex-1 flex items-center gap-1.5">
                      {saved.name === f.defaultQueryName && (
                        <Star className="h-4 w-4 fill-current shrink-0" />
                      )}
                      {saved.name}
                    </span>
                    <div className="flex gap-1">
                      <Tooltip
                        content={
                          saved.name === f.defaultQueryName
                            ? 'Stop using this query when opening this collection'
                            : 'Apply this query automatically when opening this collection'
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            saved.name === f.defaultQueryName
                              ? f.handleRemoveDefaultQuery()
                              : f.handleSetDefaultQuery(saved.name)
                          }
                        >
                          {saved.name === f.defaultQueryName ? 'Remove default' : 'Set as default'}
                        </Button>
                      </Tooltip>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => f.handleLoadQuery(saved.name)}
                      >
                        Load
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => f.handleDeleteSavedQuery(saved.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* 5. Query Preview (collapsible) */}
          <section>
            <button
              type="button"
              onClick={f.toggleQueryPreview}
              className="flex cursor-pointer items-center gap-2 w-full p-3 text-left hover:bg-muted/50 rounded-md transition-colors text-sm font-medium"
            >
              {f.queryPreviewOpen ? (
                <ChevronDown className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0" />
              )}
              {f.queryPreviewOpen ? 'View Query Code' : 'View Query Code'}
            </button>
            {f.queryPreviewOpen && f.previewQuery && (
              <div className="mt-2">
                <QueryPreview
                  query={f.previewQuery}
                  collectionName={f.collectionName}
                  hideHeading
                />
              </div>
            )}
          </section>

          {/* 6. Errors / No results */}
          {f.testRunError && (
            <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md whitespace-pre-wrap break-words">
              <QueryErrorContent text={f.testRunError} />
            </div>
          )}
          {!f.testRunError && f.error && !f.showOrError && (
            <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md whitespace-pre-wrap break-words">
              <QueryErrorContent text={f.error} />
            </div>
          )}

          {f.showNoResults && (
            <div className="p-3 bg-muted text-muted-foreground text-sm rounded-md">
              The query returned no results. Try adjusting your filters to match documents in the
              collection.
            </div>
          )}

          {/* 7. Primary actions */}
          <div className="flex flex-col gap-3 pt-4 border-t">
            <div className="flex gap-2">
              <Button onClick={f.handleRunQuery} disabled={f.runDisabled} size="lg">
                <Play className="h-4 w-4 mr-2" />
                {f.loading ? 'Running...' : f.testRunLoading ? 'Validating...' : 'Run Query'}
              </Button>
              <Button variant="ghost" onClick={f.handleClearQuery}>
                <X className="h-4 w-4 mr-2" />
                Clear Query
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Query name (optional)"
                value={f.saveQueryName}
                onChange={(e) => f.setSaveQueryName(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" onClick={f.handleSaveQuery} disabled={f.saveDisabled}>
                <Save className="h-4 w-4 mr-2" />
                {f.saveButtonLabel}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
